import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { 
  MapIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { LocationService } from '../services/LocationService';

import { thingsAPI } from '../services/api';
import UnifiedMap from '../components/UnifiedMap';
import SearchUI from '../components/SearchUI';
import HeaderSearchPortal from '../components/HeaderSearchPortal';
import { useBottomNav } from '../contexts/BottomNavContext';
import { useMobile } from '../hooks/useMobile';
import { getImageUrl } from '../config/runtime';

// Category/type filter tags for detections
const DETECTION_TAGS = [
  { id: 'nearby', label: 'Nearby', icon: '📍', description: 'Find what\'s around you' },
  { id: 'organism', label: 'Plants & Animals', icon: '🌿', description: 'Living things' },
  { id: 'fungus', label: 'Fungi', icon: '🍄', description: 'Mushrooms & fungi' },
  { id: 'artifact', label: 'Objects', icon: '🏺', description: 'Man-made items' },
  { id: 'environment', label: 'Places', icon: '🏞️', description: 'Landscapes & habitats' },
];

// Sheet height constants (as percentage of viewport)
const MOBILE_SHEET_FULL = 85;
const MOBILE_SHEET_PEEK = 45;
const MOBILE_SHEET_MAP = 10;

// Layout constants
const BOTTOM_NAV_HEIGHT = 60;
const FLOATING_BUTTON_OFFSET = BOTTOM_NAV_HEIGHT + 16;
const SEARCH_BAR_HEIGHT = 110;

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const CommunityPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile: isMobileDevice, isTablet } = useMobile();
  const { setBottomNavVisible } = useBottomNav();
  
  // Treat tablets as mobile for this page - side-by-side map doesn't work well on narrow screens
  const isMobile = isMobileDevice || isTablet;
  
  // Read view mode from URL (for back button support)
  const urlViewMode = searchParams.get('view');
  
  // View state
  const [viewMode, setViewMode] = useState('list');
  const [mobileSheetState, setMobileSheetState] = useState('list');
  const [mobileSheetHeight, setMobileSheetHeight] = useState(MOBILE_SHEET_FULL);
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [mapViewportBbox, setMapViewportBbox] = useState(null);
  const [mapZoomLevel, setMapZoomLevel] = useState(null);
  const [mapUserInteracted, setMapUserInteracted] = useState(false); // Track if user has panned/zoomed
  const [mapSyncEnabled, setMapSyncEnabled] = useState(false);
  const [mapFilterManuallyDisabled, setMapFilterManuallyDisabled] = useState(false);
  const [isRequestingNearby, setIsRequestingNearby] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [sheetAnimationsEnabled, setSheetAnimationsEnabled] = useState(false);
  
  const isMapActive = isMobile ? true : viewMode === 'map';
  const isDesktopSplitView = !isMobile && isMapActive;
  const shouldRenderMobileMap = isMobile && mobileSheetHeight < MOBILE_SHEET_FULL - 5;
  const isSheetCollapsed = mobileSheetHeight <= MOBILE_SHEET_MAP + 5;
  const mobileSheetDragState = useRef({
    active: false,
    startY: 0,
    startHeight: MOBILE_SHEET_FULL,
  });
  const mobileSheetHeightRef = useRef(mobileSheetHeight);
  const mobileSearchHeaderRef = useRef(null);
  const [mobileSearchHeaderHeight, setMobileSearchHeaderHeight] = useState(SEARCH_BAR_HEIGHT);
  const [mobileViewportHeight, setMobileViewportHeight] = useState(null);
  const effectiveMobileSearchHeight = isMobile && !isMobileSearchOpen ? mobileSearchHeaderHeight : 0;
  const viewportHeightValue = mobileViewportHeight ? `${Math.round(mobileViewportHeight)}px` : '100vh';
  const fullSheetHeightValue = isMobile
    ? `calc(${viewportHeightValue} - env(safe-area-inset-bottom, 0px) - ${Math.round(effectiveMobileSearchHeight)}px)`
    : undefined;
  const sheetTransitionClass = sheetAnimationsEnabled && !mobileSheetDragState.current.active
    ? 'transition-[height] duration-300 ease-out'
    : '';
  const sheetOpacityTransition = sheetAnimationsEnabled ? 'opacity 150ms ease-out' : 'none';
  const isNearbyActive = selectedTags.includes('nearby');
  const detectionCategoryFilters = useMemo(
    () => selectedTags.filter((tag) => tag !== 'nearby'),
    [selectedTags]
  );
  const activeDetectionCategory = detectionCategoryFilters.length > 0 ? detectionCategoryFilters[0] : null;
  
  // Selected detection for preview card
  const [selectedDetection, setSelectedDetection] = useState(null);
  
  // Scroll and observer refs
  const scrollContainerRef = useRef(null);
  const observerTarget = useRef(null);
  const geoRequestedRef = useRef(false);
  const geoWatchIdRef = useRef(null);
  const viewportChangeCountRef = useRef(0); // Count viewport changes to skip initial ones
  const formatBoundsToBbox = useCallback((bounds, fallbackCenter = null, fallbackZoom = null) => {
    const clampLat = (lat) => Math.min(90, Math.max(-90, lat));
    const clampLng = (lng) => Math.min(180, Math.max(-180, lng));
    if (
      Array.isArray(bounds) &&
      bounds.length === 2 &&
      Array.isArray(bounds[0]) &&
      Array.isArray(bounds[1])
    ) {
      const [southLat, westLng] = bounds[0];
      const [northLat, eastLng] = bounds[1];
      if (
        [southLat, westLng, northLat, eastLng].every((value) => Number.isFinite(value))
      ) {
        return `${clampLng(westLng)},${clampLat(southLat)},${clampLng(eastLng)},${clampLat(northLat)}`;
      }
    }
    if (
      fallbackCenter &&
      typeof fallbackCenter.lat === 'number' &&
      typeof fallbackCenter.lng === 'number' &&
      typeof fallbackZoom === 'number' &&
      !Number.isNaN(fallbackZoom)
    ) {
      const latRange = 180 / Math.pow(2, fallbackZoom);
      const lngRange = 360 / Math.pow(2, fallbackZoom);
      const westLng = fallbackCenter.lng - lngRange / 2;
      const eastLng = fallbackCenter.lng + lngRange / 2;
      const southLat = fallbackCenter.lat - latRange / 2;
      const northLat = fallbackCenter.lat + latRange / 2;
      return `${clampLng(westLng)},${clampLat(southLat)},${clampLng(eastLng)},${clampLat(northLat)}`;
    }
    return null;
  }, []);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerTarget.current) {
        observerTarget.current.disconnect();
      }
    };
  }, []);

  // Request geolocation
  const requestGeolocationIfNeeded = useCallback(() => {
    if (userLocation || geoRequestedRef.current) return;
    geoRequestedRef.current = true;

    LocationService.getCurrentPosition({ enableHighAccuracy: false, timeout: 3000, maximumAge: 300000 })
      .then((pos) => {
        setUserLocation({ lat: pos.lat, lng: pos.lng });
      })
      .catch((error) => {
        console.warn('Could not get location for map view:', error);
      });

    try {
      LocationService.watchPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords || {};
          if (typeof latitude === 'number' && typeof longitude === 'number') {
            setUserLocation({ lat: latitude, lng: longitude });
          }
          if (geoWatchIdRef.current && geoWatchIdRef.current.clear) {
            try { geoWatchIdRef.current.clear(); } catch {}
            geoWatchIdRef.current = null;
          }
        },
        () => {
          if (geoWatchIdRef.current && geoWatchIdRef.current.clear) {
            try { geoWatchIdRef.current.clear(); } catch {}
            geoWatchIdRef.current = null;
          }
        },
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
      ).then((sub) => { geoWatchIdRef.current = sub; });
    } catch (e) {
      console.warn('Failed to start geolocation watch:', e);
    }
  }, [userLocation]);

  // Cleanup geolocation watch
  useEffect(() => {
    return () => {
      if (geoWatchIdRef.current && geoWatchIdRef.current.clear) {
        try { geoWatchIdRef.current.clear(); } catch {}
        geoWatchIdRef.current = null;
      }
    };
  }, []);

  // Request location for nearby filter
  useEffect(() => {
    let cancelled = false;

    const fetchNearbyLocation = async () => {
      if (!isNearbyActive || userLocation || isRequestingNearby) return;

      setIsRequestingNearby(true);
      try {
        const pos = await LocationService.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        });

        if (!cancelled) {
          setUserLocation({ lat: pos.lat, lng: pos.lng });
        }
      } catch (error) {
        if (!cancelled) {
          toast.error('Turn on location to see nearby discoveries');
          setSelectedTags((prev) => prev.filter((tag) => tag !== 'nearby'));
        }
      } finally {
        if (!cancelled) {
          setIsRequestingNearby(false);
        }
      }
    };

    fetchNearbyLocation();

    return () => {
      cancelled = true;
    };
  }, [isNearbyActive, userLocation, isRequestingNearby]);

  // Delay enabling sheet animations until measurements have stabilized
  // Wait for BOTH viewport height AND search header height to be measured
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined') {
      setSheetAnimationsEnabled(true);
      return undefined;
    }
    // Don't enable until we have actual viewport measurement (not the '100vh' fallback)
    if (!mobileViewportHeight) {
      setSheetAnimationsEnabled(false);
      return undefined;
    }
    // Add a small delay after measurement to ensure everything is stable
    const timeoutId = setTimeout(() => setSheetAnimationsEnabled(true), 50);
    return () => clearTimeout(timeoutId);
  }, [isMobile, mobileViewportHeight]);

  useEffect(() => {
    mobileSheetHeightRef.current = mobileSheetHeight;
  }, [mobileSheetHeight]);

  useEffect(() => {
    if (!isMobile) {
      setMobileViewportHeight(null);
      return undefined;
    }
    if (typeof window === 'undefined') return undefined;

    const updateViewportHeight = () => {
      const viewport = window.visualViewport;
      const nextHeight = viewport?.height ?? window.innerHeight ?? document.documentElement?.clientHeight ?? 0;
      setMobileViewportHeight(nextHeight);
    };

    updateViewportHeight();

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', updateViewportHeight);
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);

    return () => {
      viewport?.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, [isMobile]);

  useIsomorphicLayoutEffect(() => {
    if (!isMobile || isMobileSearchOpen) {
      if (!isMobile) {
        setMobileSearchHeaderHeight(SEARCH_BAR_HEIGHT);
      }
      return undefined;
    }
    if (!mobileSearchHeaderRef.current || typeof window === 'undefined') {
      return undefined;
    }

    const element = mobileSearchHeaderRef.current;
    const updateHeaderHeight = () => {
      const rect = element.getBoundingClientRect();
      if (!rect?.height) return;
      const nextHeight = Math.round(rect.height);
      setMobileSearchHeaderHeight((prev) => (Math.abs(prev - nextHeight) > 1 ? nextHeight : prev));
    };

    updateHeaderHeight();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateHeaderHeight);
      resizeObserver.observe(element);
    }

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', updateHeaderHeight);
    window.addEventListener('resize', updateHeaderHeight);
    window.addEventListener('orientationchange', updateHeaderHeight);

    return () => {
      resizeObserver?.disconnect();
      viewport?.removeEventListener('resize', updateHeaderHeight);
      window.removeEventListener('resize', updateHeaderHeight);
      window.removeEventListener('orientationchange', updateHeaderHeight);
    };
  }, [isMobile, isMobileSearchOpen]);

  // Hide bottom nav when sheet is collapsed or detection preview is open
  useEffect(() => {
    if (isMobile) {
      setBottomNavVisible(!isSheetCollapsed && !selectedDetection);
    }
    return () => {
      if (isMobile) {
        setBottomNavVisible(true);
      }
    };
  }, [isMobile, isSheetCollapsed, selectedDetection, setBottomNavVisible]);

  useEffect(() => {
    if (!isMobile) {
      setMobileSheetState('list');
      setMobileSheetHeight(MOBILE_SHEET_FULL);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobileSearchOpen) {
      setMobileSearchTerm(searchTerm);
    }
  }, [isMobileSearchOpen, searchTerm]);

  useEffect(() => {
    if (!isMobile) return undefined;
    if (isMobileSearchOpen) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previous;
      };
    }
    return undefined;
  }, [isMobile, isMobileSearchOpen]);

  useEffect(() => {
    if (viewMode === 'map') {
      requestGeolocationIfNeeded();
    }
  }, [viewMode, requestGeolocationIfNeeded]);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setMobileSheetState('list');
      return;
    }
    requestGeolocationIfNeeded();
  }, [isMobile, requestGeolocationIfNeeded]);

  // Handlers
  const handleNearbyToggle = useCallback(() => {
    setSelectedTags((prev) => {
      if (prev.includes('nearby')) {
        setIsRequestingNearby(false);
        return prev.filter((tag) => tag !== 'nearby');
      }
      return [...prev, 'nearby'];
    });
  }, []);

  const openMobileSearch = useCallback(() => {
    setIsMobileSearchOpen(true);
  }, []);

  const closeMobileSearch = useCallback(() => {
    setIsMobileSearchOpen(false);
  }, []);

  const handleDesktopViewChange = useCallback((mode) => {
    if (mode === 'map') {
      setViewMode('map');
      viewportChangeCountRef.current = 0; // Reset counter for new map view
      requestGeolocationIfNeeded();
      setMapFilterManuallyDisabled(false);
    } else {
      setViewMode('list');
      viewportChangeCountRef.current = 0;
    }
  }, [requestGeolocationIfNeeded]);

  // Ref to track last URL view we synced, prevents sync loops
  // Initialize to undefined so first sync always runs
  const lastSyncedUrlViewRef = useRef(undefined);
  
  const handleMobileViewChange = useCallback((mode, { updateUrl = true } = {}) => {
    setMobileSheetState(mode);
    if (mode === 'map') {
      setMobileSheetHeight(MOBILE_SHEET_MAP);
      viewportChangeCountRef.current = 0; // Reset counter for new map view
      requestGeolocationIfNeeded();
      setMapFilterManuallyDisabled(false);
      // Add to browser history so back button returns to list view
      if (updateUrl) {
        lastSyncedUrlViewRef.current = 'map'; // Prevent sync effect from re-running
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('view', 'map');
          return next;
        });
      }
    } else {
      setMobileSheetHeight(MOBILE_SHEET_FULL);
      viewportChangeCountRef.current = 0;
      setSelectedDetection(null);
      // Remove view param from URL
      if (updateUrl) {
        lastSyncedUrlViewRef.current = null; // Prevent sync effect from re-running
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('view');
          return next;
        });
      }
    }
  }, [requestGeolocationIfNeeded, setSearchParams]);

  const handleMobileViewToggle = useCallback(() => {
    handleMobileViewChange(mobileSheetState === 'list' ? 'map' : 'list');
  }, [handleMobileViewChange, mobileSheetState]);

  // Sync sheet state from URL on mount and back button (popstate)
  // Only sync when URL actually changes, not on every state change
  useEffect(() => {
    if (!isMobile) return;
    // Only sync if URL actually changed (back/forward button or direct navigation)
    if (urlViewMode === lastSyncedUrlViewRef.current) return;
    lastSyncedUrlViewRef.current = urlViewMode;
    
    const targetMode = urlViewMode === 'map' ? 'map' : 'list';
    // Don't update URL since we're syncing FROM the URL
    handleMobileViewChange(targetMode, { updateUrl: false });
  }, [isMobile, urlViewMode, handleMobileViewChange]);

  const handleClearMapAreaFilter = useCallback(() => {
    setMapSyncEnabled(false);
    setMapFilterManuallyDisabled(true);
  }, []);

  const handleEnableMapAreaFilter = useCallback(() => {
    if (!mapViewportBbox) return;
    setMapFilterManuallyDisabled(false);
    setMapSyncEnabled(true);
  }, [mapViewportBbox]);

  // Sheet drag handlers
  const handleSheetDragMove = useCallback((event) => {
    if (!mobileSheetDragState.current.active) return;
    event.preventDefault();

    const viewportHeight = typeof window !== 'undefined'
      ? (window.visualViewport?.height || window.innerHeight || 1)
      : 1;

    const clientY = event.clientY ?? mobileSheetDragState.current.startY;
    const deltaY = mobileSheetDragState.current.startY - clientY;
    const deltaPercent = (deltaY / (viewportHeight || 1)) * 100;
    const nextHeight = Math.min(
      MOBILE_SHEET_FULL + 5,
      Math.max(MOBILE_SHEET_MAP - 2, mobileSheetDragState.current.startHeight + deltaPercent)
    );

    setMobileSheetHeight(nextHeight);
  }, []);

  const handleSheetDragEnd = useCallback(() => {
    if (!mobileSheetDragState.current.active) return;
    mobileSheetDragState.current.active = false;

    if (typeof document !== 'undefined') {
      document.removeEventListener('pointermove', handleSheetDragMove);
      document.removeEventListener('pointerup', handleSheetDragEnd);
      document.removeEventListener('pointercancel', handleSheetDragEnd);
      document.body.style.userSelect = '';
    }

    const currentHeight = mobileSheetHeightRef.current;
    const snapPoints = [MOBILE_SHEET_MAP, MOBILE_SHEET_PEEK, MOBILE_SHEET_FULL];
    
    let closestSnap = snapPoints[0];
    let closestDistance = Math.abs(currentHeight - snapPoints[0]);
    
    for (const snap of snapPoints) {
      const distance = Math.abs(currentHeight - snap);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestSnap = snap;
      }
    }
    
    setMobileSheetHeight(closestSnap);
    const newState = closestSnap === MOBILE_SHEET_MAP ? 'map' : 'list';
    setMobileSheetState(newState);
    
    // Update URL to match drag result (for back button support)
    if (closestSnap === MOBILE_SHEET_MAP) {
      // Dragged to collapsed/map view
      lastSyncedUrlViewRef.current = 'map';
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', 'map');
        return next;
      });
    } else if (closestSnap === MOBILE_SHEET_FULL || closestSnap === MOBILE_SHEET_PEEK) {
      // Dragged to expanded view - remove map param if present
      lastSyncedUrlViewRef.current = null;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (next.has('view')) {
          next.delete('view');
          return next;
        }
        return prev; // Don't update if no change needed
      });
    }
    
    if (closestSnap === MOBILE_SHEET_FULL) {
      setSelectedDetection(null);
    }
  }, [handleSheetDragMove, setSearchParams]);

  const handleSheetPointerDown = useCallback((event) => {
    if (!isMobile || mobileSheetDragState.current.active) return;
    event.preventDefault();

    mobileSheetDragState.current = {
      active: true,
      startY: event.clientY ?? 0,
      startHeight: mobileSheetHeightRef.current,
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('pointermove', handleSheetDragMove);
      document.addEventListener('pointerup', handleSheetDragEnd);
      document.addEventListener('pointercancel', handleSheetDragEnd);
      document.body.style.userSelect = 'none';
    }
  }, [isMobile, handleSheetDragMove, handleSheetDragEnd]);

  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('pointermove', handleSheetDragMove);
        document.removeEventListener('pointerup', handleSheetDragEnd);
        document.removeEventListener('pointercancel', handleSheetDragEnd);
      }
    };
  }, [handleSheetDragMove, handleSheetDragEnd]);

  // Build query parameters for ThingsDB API
  const isMapVisible = useMemo(
    () => shouldRenderMobileMap || (!isMobile && viewMode === 'map'),
    [isMobile, shouldRenderMobileMap, viewMode]
  );

  const buildQueryParams = useCallback((pageParam = 1) => {
    const params = {
      page: pageParam,
      limit: 50,
      layer: 'detections',
      visibility: 'public', // Only show public project detections
      cluster: true, // Enable server-side clustering for performance
    };
    
    if (activeDetectionCategory) {
      params.category = activeDetectionCategory;
    }
    
    if (searchTerm) params.search = searchTerm;
    
    if (mapViewportBbox && isMapVisible && mapUserInteracted && mapSyncEnabled) {
      params.bbox = mapViewportBbox;
    } else if (userLocation && selectedTags.includes('nearby')) {
      // For nearby filter without map, create a bbox around user location
      const radius = 0.5; // ~50km in degrees
      params.bbox = `${userLocation.lng - radius},${userLocation.lat - radius},${userLocation.lng + radius},${userLocation.lat + radius}`;
    }
    
    return params;
  }, [
    activeDetectionCategory,
    isMapVisible,
    mapSyncEnabled,
    mapUserInteracted,
    mapViewportBbox,
    searchTerm,
    selectedTags,
    userLocation,
  ]);

  // Query key for caching - include bbox when map is visible and user has interacted
  const queryKeyBbox = (isMapVisible && mapUserInteracted && mapSyncEnabled) ? mapViewportBbox : (selectedTags.includes('nearby') ? userLocation : null);

  // Fetch detections with infinite query
  const { 
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch 
  } = useInfiniteQuery({
    queryKey: ['community-detections', searchTerm, selectedTags, queryKeyBbox, mapUserInteracted, mapSyncEnabled],
    queryFn: async ({ pageParam = 1 }) => {
      const params = buildQueryParams(pageParam);
      const response = await thingsAPI.getItems(params);
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage?.pagination;
      const items = lastPage?.things || [];
      const requestedLimit = 50;
      
      if (items.length === 0) return undefined;
      if (items.length < requestedLimit) return undefined;
      if (pagination && pagination.total <= allPages.length * requestedLimit) return undefined;
      if (allPages.length >= 10) return undefined;
      
      return allPages.length + 1;
    },
    staleTime: 5 * 60 * 1000,
  });

  const mapZoomBucket = useMemo(() => (mapZoomLevel && mapZoomLevel >= 11 ? 'detail' : 'cluster'), [mapZoomLevel]);
  const mapViewportLimit = mapZoomBucket === 'detail' ? 500 : 200;

  const buildMapQueryParams = useCallback(() => {
    if (!mapViewportBbox) return null;
    const params = {
      layer: 'detections',
      visibility: 'public',
      cluster: true,
      bbox: mapViewportBbox,
      limit: mapZoomBucket === 'detail' ? 500 : 200,
    };
    if (activeDetectionCategory) {
      params.category = activeDetectionCategory;
    }
    if (searchTerm) {
      params.search = searchTerm;
    }
    return params;
  }, [activeDetectionCategory, mapViewportBbox, mapZoomBucket, searchTerm]);

  const mapQueryEnabled = isMapVisible && Boolean(mapViewportBbox);
  const { data: mapData } = useQuery({
    queryKey: ['community-map', mapViewportBbox, searchTerm, activeDetectionCategory, mapZoomBucket],
    queryFn: async () => {
      const params = buildMapQueryParams();
      if (!params) return null;
      const response = await thingsAPI.getItems(params);
      return response.data;
    },
    enabled: mapQueryEnabled,
    keepPreviousData: true,
  });

  // Flatten all pages into a single detections array
  const detections = useMemo(() => {
    return data?.pages.reduce((acc, page) => {
      return [...acc, ...(page?.things || [])];
    }, []) || [];
  }, [data]);

  const mapDetectionSource = useMemo(() => {
    if (mapData?.things && mapData.things.length > 0) {
      return mapData.things;
    }
    if (!mapQueryEnabled) {
      return detections;
    }
    return mapData?.things || [];
  }, [detections, mapData, mapQueryEnabled]);

  const detectionPins = useMemo(() => {
    return mapDetectionSource
      .filter((d) => Number.isFinite(d.lat) && Number.isFinite(d.lng))
      .map((d) => ({
        id: d.id,
        lat: d.lat,
        lng: d.lng,
        name: d.canonical_name || d.label || 'Unknown',
        imageUrl: d.sample_images?.[0] || null,
        count: 1,
        type: d.type || d.category || 'detection',
        _detection: d,
      }));
  }, [mapDetectionSource]);

  const rawMapClusters = useMemo(() => mapData?.clusters || [], [mapData]);
  const shouldShowClusters = rawMapClusters.length > 0 && mapZoomBucket === 'cluster';

  const clusterPins = useMemo(() => {
    if (!shouldShowClusters) return [];
    return rawMapClusters
      .filter((cluster) => Number.isFinite(cluster?.lat) && Number.isFinite(cluster?.lng))
      .map((cluster, index) => ({
        id: `cluster-${index}-${cluster.lat}-${cluster.lng}`,
        lat: cluster.lat,
        lng: cluster.lng,
        count: Math.max(1, cluster.count || 1),
        name: `${cluster.count || 0} discoveries`,
        imageUrl: null,
        type: 'cluster',
        _cluster: true,
      }));
  }, [rawMapClusters, shouldShowClusters]);

  const mapPins = useMemo(() => {
    if (clusterPins.length > 0) {
      return clusterPins;
    }
    return detectionPins;
  }, [clusterPins, detectionPins]);

  useEffect(() => {
    if (clusterPins.length > 0 && selectedDetection) {
      setSelectedDetection(null);
    }
  }, [clusterPins.length, selectedDetection]);

  // Calculate map bounds from detections with valid coordinates
  const mapBounds = useMemo(() => {
    const boundsSource = detectionPins.length > 0 ? detectionPins : mapPins;
    const validPins = boundsSource.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (validPins.length === 0) return null;
    
    const lats = validPins.map(p => p.lat);
    const lngs = validPins.map(p => p.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    // Add padding (10% of the range)
    const latPadding = (maxLat - minLat) * 0.1 || 0.01;
    const lngPadding = (maxLng - minLng) * 0.1 || 0.01;
    
    return [
      [minLat - latPadding, minLng - lngPadding], // Southwest
      [maxLat + latPadding, maxLng + lngPadding], // Northeast
    ];
  }, [detectionPins, mapPins]);

  // Intersection Observer for infinite scroll
  const observeLastDetection = useCallback(
    (node) => {
      if (isLoading || isFetchingNextPage) return;
      if (observerTarget.current) observerTarget.current.disconnect();
      
      observerTarget.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }, { threshold: 0.1 });
      
      if (node) observerTarget.current.observe(node);
    },
    [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  // Search handlers
  const handleSearch = useCallback(() => {
    setSearchTerm(mobileSearchTerm);
  }, [mobileSearchTerm]);

  const applyMobileSearch = useCallback(() => {
    handleSearch();
    setIsMobileSearchOpen(false);
  }, [handleSearch]);

  const handleClearAll = useCallback(() => {
    setSearchTerm('');
    setMobileSearchTerm('');
    setSelectedTags([]);
    setMapSyncEnabled(false);
    setMapFilterManuallyDisabled(false);
  }, []);

  // Navigate to project when clicking a detection
  const handleDetectionClick = useCallback((detection) => {
    if (!detection?.id) return;
    navigate(`/discover/${detection.id}`, {
      state: {
        previousPath: '/discover',
        transitionDirection: 'forward'
      }
    });
  }, [navigate]);

  // Summary labels
  const mapAreaFilterActive = mapSyncEnabled && Boolean(mapViewportBbox);
  const mapViewportResultCount = mapData?.pagination?.total ?? mapDetectionSource.length;
  const mapResultsCapped = Boolean(
    mapData?.pagination?.total && mapData.pagination.total > mapViewportLimit
  );
  const totalDetectionsLabel = data?.pages[0]?.pagination?.total ?? detections.length;
  const hasActiveFilters = Boolean(
    searchTerm?.trim() || selectedTags.length > 0 || mapAreaFilterActive
  );
  const selectedTagLabels = selectedTags
    .filter((tag) => tag !== 'nearby')
    .map((tag) => DETECTION_TAGS.find((t) => t.id === tag)?.label)
    .filter(Boolean);
  const filterSummaryParts = [];
  if (mapAreaFilterActive) filterSummaryParts.push('Map area');
  if (isNearbyActive) filterSummaryParts.push('Nearby');
  if (selectedTagLabels.length > 0) {
    const preview = selectedTagLabels.slice(0, 2).join(', ');
    const extra = selectedTagLabels.length > 2 ? ` +${selectedTagLabels.length - 2}` : '';
    filterSummaryParts.push(`${preview}${extra}`);
  }
  const mobileFilterSummary = filterSummaryParts.length > 0 ? filterSummaryParts.join(' · ') : 'Any filters';
  const mobileSearchSummary = (() => {
    if (searchTerm?.trim()) return searchTerm;
    if (mapAreaFilterActive) return 'Results in map area';
    return 'All Discoveries';
  })();

  // Mobile search overlay
  const mobileSearchOverlay = hasMounted && isMobile && isMobileSearchOpen && typeof document !== 'undefined'
    ? createPortal(
        <div className="fixed inset-0 z-[2147483647] bg-white dark:bg-gray-900" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-4 pt-4 mb-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Search Discoveries</h2>
              <button
                onClick={closeMobileSearch}
                className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 flex items-center justify-center shadow-sm border border-gray-200 dark:border-gray-700 shadow-lg"
                aria-label="Close search"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-shrink-0 px-4 space-y-4">
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search discoveries (e.g. mushrooms, birds)"
                  value={mobileSearchTerm}
                  onChange={(e) => setMobileSearchTerm(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      applyMobileSearch();
                    }
                  }}
                  className="block w-full pl-10 pr-10 py-4 border border-gray-300 dark:border-gray-600 rounded-xl leading-5 bg-white text-sm dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  autoFocus
                />
                {mobileSearchTerm && (
                  <button
                    type="button"
                    onClick={() => setMobileSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    aria-label="Clear search"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  </button>
                )}
              </div>
            </div>
            
            <div className="flex-1 min-h-0 px-4 pt-4 relative">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Filter by category</p>
              <div 
                className="absolute left-4 right-4 bottom-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden"
                style={{ top: 'calc(1rem + 1.5rem + 0.75rem)' }}
              >
                <div 
                  className="overflow-y-auto mobile-scroll"
                  style={{ 
                    height: '100%',
                    WebkitOverflowScrolling: 'touch',
                    touchAction: 'pan-y'
                  }}
                >
                  {DETECTION_TAGS.map((tag) => {
                    const isSelected = selectedTags.includes(tag.id);
                    const isNearbyTag = tag.id === 'nearby';
                    return (
                      <button
                        key={tag.id}
                        onClick={() => {
                          if (isNearbyTag) {
                            handleNearbyToggle();
                          } else {
                            if (selectedTags.includes(tag.id)) {
                              setSelectedTags(selectedTags.filter(t => t !== tag.id));
                            } else {
                              setSelectedTags([...selectedTags.filter(t => t === 'nearby'), tag.id]);
                            }
                          }
                        }}
                        className={`
                          w-full flex items-center gap-4 px-4 py-3.5 text-left
                          hover:bg-gray-50 dark:hover:bg-gray-700
                          transition-colors duration-150
                          ${isSelected ? 'bg-green-50 dark:bg-green-900/20' : ''}
                        `}
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                          <span className="text-xl">{tag.icon}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`font-semibold text-sm ${isSelected ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>
                            {tag.label}
                          </div>
                          {tag.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {tag.description}
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full"></div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div
              className="px-6 py-4 mb-2 border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)' }}
            >
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={handleClearAll}
                  className="text-sm font-semibold text-gray-700 dark:text-gray-200 underline"
                >
                  Clear all
                </button>
                <button
                  onClick={applyMobileSearch}
                  className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-white font-semibold text-base bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 shadow-lg min-w-[120px]"
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  // Detection Card component with bounding box overlay
  const DetectionCard = ({ detection, isNearEnd, observerRef }) => {
    const imageUrl = detection.sample_images?.[0] ? getImageUrl(detection.sample_images[0]) : null;
    const displayName = detection.canonical_name || detection.label || 'Unknown discovery';
    const confidence = detection.confidence ? Math.round(detection.confidence * 100) : null;
    const capturedAt = detection.last_seen_at ? new Date(detection.last_seen_at) : null;
    const [imageDims, setImageDims] = useState(null);
    const containerRef = useRef(null);
    
    // Get the primary bbox (from bbox or first item in bboxes)
    const bbox = detection.bbox || (detection.bboxes?.[0]) || null;
    
    // Calculate bbox overlay style accounting for object-cover crop
    const getBboxStyle = () => {
      if (!bbox || !imageDims || !containerRef.current) return null;
      const { x1, y1, x2, y2 } = bbox;
      const { naturalWidth, naturalHeight } = imageDims;
      
      if (!naturalWidth || !naturalHeight) return null;
      
      // Get container dimensions (square)
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerSize = containerRect.width; // Square, so width = height
      
      // Calculate how object-cover scales and positions the image
      const imageAspect = naturalWidth / naturalHeight;
      const containerAspect = 1; // Square
      
      let scale, offsetX, offsetY;
      
      if (imageAspect > containerAspect) {
        // Image is wider - height fills container, width is cropped
        scale = containerSize / naturalHeight;
        const scaledWidth = naturalWidth * scale;
        offsetX = (scaledWidth - containerSize) / 2;
        offsetY = 0;
      } else {
        // Image is taller - width fills container, height is cropped
        scale = containerSize / naturalWidth;
        const scaledHeight = naturalHeight * scale;
        offsetX = 0;
        offsetY = (scaledHeight - containerSize) / 2;
      }
      
      // Transform bbox coordinates to container space
      const left = (x1 * scale - offsetX) / containerSize * 100;
      const top = (y1 * scale - offsetY) / containerSize * 100;
      const width = ((x2 - x1) * scale) / containerSize * 100;
      const height = ((y2 - y1) * scale) / containerSize * 100;
      
      // Skip if bbox covers nearly the whole image (>85%) - not useful to show
      if (width > 85 && height > 85) return null;
      
      // Skip if bbox is completely outside visible area
      if (left + width < 0 || left > 100 || top + height < 0 || top > 100) return null;
      
      // Clamp to visible area
      const clampedLeft = Math.max(0, left);
      const clampedTop = Math.max(0, top);
      const clampedWidth = Math.min(100 - clampedLeft, width - (clampedLeft - left));
      const clampedHeight = Math.min(100 - clampedTop, height - (clampedTop - top));
      
      // Skip if resulting box is too small after clamping
      if (clampedWidth < 5 || clampedHeight < 5) return null;
      
      return {
        left: `${clampedLeft}%`,
        top: `${clampedTop}%`,
        width: `${clampedWidth}%`,
        height: `${clampedHeight}%`,
      };
    };
    
    const bboxStyle = getBboxStyle();
    
    return (
      <div
        ref={isNearEnd ? observerRef : null}
        className="group cursor-pointer"
        onClick={() => handleDetectionClick(detection)}
      >
        {/* Image */}
        <div 
          ref={containerRef}
          className="relative aspect-square overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800"
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={displayName}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onLoad={(e) => setImageDims({
                naturalWidth: e.target.naturalWidth,
                naturalHeight: e.target.naturalHeight
              })}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl">
              🔍
            </div>
          )}
          {/* Bounding box overlay */}
          {bboxStyle && (
            <div 
              className="absolute border-2 border-green-400/80 rounded pointer-events-none shadow-sm"
              style={bboxStyle}
            />
          )}
          {/* Confidence badge */}
          {confidence && (
            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
              {confidence}%
            </div>
          )}
          {/* Category badge */}
          {detection.category && (
            <div className="absolute top-2 left-2 bg-white/90 dark:bg-gray-800/90 text-gray-900 dark:text-white text-xs px-2 py-1 rounded-full capitalize">
              {detection.category}
            </div>
          )}
        </div>
        
        {/* Info */}
        <div className="pt-3">
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-1">
            {displayName}
          </h3>
          
          {/* User who found it and date */}
          <div className="flex items-center gap-1.5 mt-1">
            {detection.user_avatar ? (
              <img
                src={getImageUrl(detection.user_avatar)}
                alt={detection.user_display || detection.username || 'User'}
                className="w-4 h-4 rounded-full object-cover"
              />
            ) : (
              <UserCircleIcon className="w-4 h-4 text-gray-400" />
            )}
            <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
              {detection.user_display || detection.username || 'Anonymous'}
            </span>
            {capturedAt && (
              <>
                <span className="text-gray-400">·</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {capturedAt.toLocaleDateString()}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // List pane
  const listPane = (
    <div 
      ref={scrollContainerRef}
      className="relative h-full overflow-y-auto mobile-scroll"
      style={{
        paddingBottom: isMobile ? `calc(env(safe-area-inset-bottom, 0px) + 120px)` : undefined,
      }}
    >
      <div className={`px-6 ${isMobile ? 'pt-3 pb-2 space-y-4' : 'pb-4 sm:pb-4 sm:pt-4 sticky bg-white dark:bg-black top-0 z-[10000]'}`}>
        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-between gap-4'}`}>
          <div>
            {!isMobile && (
              <div className="flex flex-col gap-1">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Discover Projects</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">See what others are working on</p>
              </div>
            )}
            {isMobile && mobileSheetHeight >= MOBILE_SHEET_FULL - 5 && (
              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">Discover Projects</h1>
                  {/* <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{projects.length}</p> */}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">See what others are working on</p>
              </div>
            )}
          </div>
          <div className={`flex ${isMobile ? 'flex-col gap-3 w-full' : 'items-center gap-3 flex-wrap justify-end'}`}>
            {!isMobile && (
              <>
                <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1 text-sm font-medium">
                  <button
                    onClick={() => handleDesktopViewChange('list')}
                    className={`px-4 py-1.5 rounded-full transition-all ${
                      viewMode === 'list'
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    List
                  </button>
                  <button
                    onClick={() => handleDesktopViewChange('map')}
                    className={`px-4 py-1.5 rounded-full transition-all flex items-center gap-1 ${
                      viewMode === 'map'
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    <MapIcon className="w-4 h-4" />
                    Map
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {mapViewportBbox && (
        <div className="px-5 mb-4">
          {mapAreaFilterActive ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-2xl border border-green-200 dark:border-green-800 bg-green-50/80 dark:bg-green-900/10 px-4 py-3">
              <div className="text-sm text-green-800 dark:text-green-200">
                Showing discoveries inside the current map area
              </div>
              <button
                onClick={handleClearMapAreaFilter}
                className="text-sm font-semibold text-green-700 dark:text-green-200 hover:text-green-900 dark:hover:text-white"
              >
                Show global results
              </button>
            </div>
          ) : (
            mapUserInteracted && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-4 py-3">
                <div className="text-sm text-gray-700 dark:text-gray-200">
                  Map area detected. Apply it to the list?
                </div>
                <button
                  onClick={handleEnableMapAreaFilter}
                  className="text-sm font-semibold text-gray-900 dark:text-white hover:underline"
                >
                  Use map area
                </button>
              </div>
            )
          )}
        </div>
      )}

      <div className="px-5 py-6 pt-2 pb-32">
        {isLoading ? (
          <div className={`grid gap-5 ${
            isDesktopSplitView 
              ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4' 
              : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6'
          }`}>
            {[...Array(12)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="aspect-square bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                <div className="pt-3 space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-600 dark:text-red-400 mb-4">Failed to load discoveries</div>
            <button
              onClick={() => refetch()}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : detections.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🔍</div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No discoveries found</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {hasActiveFilters 
                ? 'Try adjusting your search or filters' 
                : 'Be the first to share a discovery!'}
            </p>
          </div>
        ) : (
          <>
            <div className={`grid gap-5 ${
              isDesktopSplitView 
                ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4' 
                : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6'
            }`}>
              {detections.map((detection, index) => {
                const isNearEnd = index >= detections.length - 6;
                return (
                  <DetectionCard
                    key={detection.id}
                    detection={detection}
                    isNearEnd={isNearEnd}
                    observerRef={observeLastDetection}
                  />
                );
              })}
            </div>
            {isFetchingNextPage && (
              <div className="flex justify-center items-center py-8">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 dark:border-green-400"></div>
                  <span className="text-gray-600 dark:text-gray-400 text-sm">Loading more...</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      <HeaderSearchPortal>
        <SearchUI
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          tags={DETECTION_TAGS}
          searchPlaceholder="Search discoveries"
          onSearch={handleSearch}
          onClearAll={handleClearAll}
          className="w-full"
        />
      </HeaderSearchPortal>
      
      {isMobile && !isMobileSearchOpen && (
        <div
          ref={mobileSearchHeaderRef}
          className="fixed pb-3 px-6 top-0 left-0 right-0 z-50 pointer-events-auto bg-white dark:bg-black"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <button
            onClick={openMobileSearch}
            className="mt-3 w-full bg-white/95 dark:bg-gray-700 border border-gray-200 dark:border-gray-600/50 rounded-full px-4 py-3 shadow-lg flex items-center gap-3"
          >
            {!searchTerm?.trim() && selectedTags.length === 0 && !mapAreaFilterActive ? (
              <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300 text-center py-2">
                <div className="flex items-center justify-center gap-2">
                  <MagnifyingGlassIcon className="w-4 h-4 text-black dark:text-gray-300" />
                  Search discoveries
                </div>
              </span>
            ) : (
              <div className="flex-1 text-center">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{mobileSearchSummary}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {mapAreaFilterActive 
                    ? (mobileFilterSummary !== 'Any filters' ? `Map area · ${mobileFilterSummary}` : 'Filtered by map area')
                    : mobileFilterSummary
                  }
                </p>
              </div>
            )}
          </button>
        </div>
      )}

      <div className="h-full flex flex-col bg-white dark:bg-gray-900 lg:relative lg:overflow-hidden overflow-hidden" style={{ minHeight: '100dvh' }}>
        {!isMobile ? (
          <div className="flex-1 overflow-hidden">
            <div className={`h-full ${isDesktopSplitView ? 'lg:grid lg:grid-cols-[minmax(0,_4fr)_minmax(0,_6fr)] lg:gap-0' : ''}`}>
              <div className={`w-full h-full ${isDesktopSplitView ? 'lg:border-r lg:border-gray-200 dark:lg:border-gray-800 lg:min-h-0' : ''}`}>
                {listPane}
              </div>

              {isMapActive && (
                <div className="w-full relative lg:block lg:h-full lg:min-h-0">
                  <UnifiedMap 
                    mode="view"
                    hotspots={mapPins}
                    showHotspots={true}
                    userLocation={userLocation}
                    layoutContext="explore"
                    bounds={mapBounds}
                    autoCenterOnUser={!mapBounds}
                    onHotspotSelect={(hotspot) => {
                      if (hotspot?._cluster) {
                        return;
                      }
                      const detection = hotspot._detection || hotspot;
                      setSelectedDetection(detection);
                    }}
                    selectedHotspotId={selectedDetection?.id}
                    onViewportChange={(center, zoom, _radius, bounds) => {
                      setMapZoomLevel(typeof zoom === 'number' ? zoom : null);
                      const bbox = formatBoundsToBbox(bounds, center, zoom);
                      if (bbox) {
                        setMapViewportBbox((prev) => (prev === bbox ? prev : bbox));
                      }
                      
                      viewportChangeCountRef.current += 1;
                      if (viewportChangeCountRef.current > 3) {
                        setMapUserInteracted(true);
                        if (mapFilterManuallyDisabled) {
                          setMapFilterManuallyDisabled(false);
                        }
                        if (!mapFilterManuallyDisabled) {
                          setMapSyncEnabled(true);
                        }
                      }
                      
                      if (selectedDetection) {
                        setSelectedDetection(null);
                      }
                    }}
                  />
                  {/* Desktop detection preview card */}
                  {selectedDetection && (
                    <div className="absolute bottom-4 left-4 right-4 max-w-sm z-50">
                      <div 
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden cursor-pointer hover:shadow-2xl transition-shadow"
                        onClick={() => handleDetectionClick(selectedDetection)}
                      >
                        <div className="flex gap-3 p-3">
                          {selectedDetection.sample_images?.[0] ? (
                            <img
                              src={getImageUrl(selectedDetection.sample_images[0])}
                              alt={selectedDetection.canonical_name || 'Discovery'}
                              className="w-20 h-20 object-cover rounded-xl flex-shrink-0"
                            />
                          ) : (
                            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-xl flex-shrink-0 flex items-center justify-center text-2xl">
                              🔍
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-1">
                              {selectedDetection.canonical_name || selectedDetection.label || 'Unknown discovery'}
                            </h3>
                            {selectedDetection.user_display && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                Found by {selectedDetection.user_display}
                              </p>
                            )}
                            {selectedDetection.project_name && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                                in {selectedDetection.project_name}
                              </p>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDetection(null);
                              }}
                              className="mt-2 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {!isLoading && !selectedDetection && (
                    <div className="absolute bottom-4 right-4 bg-black/75 dark:bg-gray-800/90 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                      <span>
                        {(mapViewportResultCount ?? detections.length)} discover{(mapViewportResultCount ?? detections.length) !== 1 ? 'ies' : 'y'}
                      </span>
                      {mapResultsCapped && (
                        <p className="text-[11px] text-gray-200/90 mt-0.5">
                          Zoom in to see more
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="relative flex-1 w-full">
            {/* Map Layer */}
            {shouldRenderMobileMap && (
              <div className="absolute inset-0 z-0">
                <UnifiedMap 
                  mode="view"
                  hotspots={mapPins}
                  showHotspots={true}
                  userLocation={userLocation}
                  layoutContext="explore"
                  bounds={mapBounds}
                  autoCenterOnUser={!mapBounds}
                  onHotspotSelect={(hotspot) => {
                    if (hotspot?._cluster) {
                      return;
                    }
                    const detection = hotspot._detection || hotspot;
                    setSelectedDetection(detection);
                  }}
                  selectedHotspotId={selectedDetection?.id}
                  onViewportChange={(center, zoom, _radius, bounds) => {
                    setMapZoomLevel(typeof zoom === 'number' ? zoom : null);
                    const bbox = formatBoundsToBbox(bounds, center, zoom);
                    if (bbox) {
                      setMapViewportBbox((prev) => (prev === bbox ? prev : bbox));
                    }
                    
                    viewportChangeCountRef.current += 1;
                    if (viewportChangeCountRef.current > 3) {
                      setMapUserInteracted(true);
                      if (mapFilterManuallyDisabled) {
                        setMapFilterManuallyDisabled(false);
                      }
                      if (!mapFilterManuallyDisabled) {
                        setMapSyncEnabled(true);
                      }
                    }
                    
                    if (selectedDetection) {
                      setSelectedDetection(null);
                    }
                  }}
                />
                {/* {!isLoading && !selectedDetection && (
                  <div 
                    className="absolute right-4 bg-black/80 text-white px-3 py-1.5 rounded-2xl text-xs font-semibold shadow-lg flex flex-col items-center"
                    style={{ top: 'calc(env(safe-area-inset-top, 0px) + 80px)' }}
                  >
                    <span>
                      {(mapViewportResultCount ?? detections.length)} result{(mapViewportResultCount ?? detections.length) !== 1 ? 's' : ''}
                    </span>
                    {mapResultsCapped && (
                      <span className="text-[10px] font-normal text-white/80">
                        Zoom in for more
                      </span>
                    )}
                  </div>
                )} */}
              </div>
            )}

            {/* Detection Preview Card */}
            {selectedDetection && shouldRenderMobileMap && (
              <div 
                className="fixed left-3 right-3 z-[600]"
                style={{
                  bottom: `calc(env(safe-area-inset-bottom, 0px) + 16px)`,
                  animation: 'slideUp 0.25s ease-out',
                }}
              >
                <div 
                  className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
                  onClick={() => handleDetectionClick(selectedDetection)}
                >
                  <div className="relative">
                    {selectedDetection.sample_images?.[0] ? (
                      <img
                        src={getImageUrl(selectedDetection.sample_images[0])}
                        alt={selectedDetection.canonical_name || 'Discovery'}
                        className="w-full h-44 object-cover"
                      />
                    ) : (
                      <div className="w-full h-44 bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-4xl">
                        🔍
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDetection(null);
                      }}
                      className="absolute top-3 right-3 w-8 h-8 bg-white/90 dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-md backdrop-blur-sm"
                      aria-label="Close"
                    >
                      <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-gray-200" />
                    </button>
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-base">
                      {selectedDetection.canonical_name || selectedDetection.label || 'Unknown discovery'}
                    </h3>
                    {selectedDetection.user_display && (
                      <div className="flex items-center gap-1.5 mt-1">
                        {selectedDetection.user_avatar ? (
                          <img src={selectedDetection.user_avatar} alt="" className="w-4 h-4 rounded-full" />
                        ) : (
                          <UserCircleIcon className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Found by {selectedDetection.user_display}
                        </span>
                      </div>
                    )}
                    {selectedDetection.project_name && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        in {selectedDetection.project_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Map Toggle Button */}
            {mobileSheetHeight >= MOBILE_SHEET_FULL - 5 && !selectedDetection && (
              <div 
                className="fixed left-1/2 -translate-x-1/2 z-[1000]"
                style={{ bottom: `calc(env(safe-area-inset-bottom, 0px) + ${FLOATING_BUTTON_OFFSET}px + 8px)` }}
              >
                <button
                  onClick={handleMobileViewToggle}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold shadow-xl active:scale-95 transition-transform"
                >
                  <MapIcon className="w-4 h-4" />
                  Map
                </button>
              </div>
            )}

            {/* Bottom Sheet */}
            <div 
              className={`absolute inset-x-0 bottom-0 z-[500] transition-all duration-300 ease-out ${
                selectedDetection ? 'opacity-0 pointer-events-none' : 'opacity-100 pointer-events-none'
              } ${
                mobileSheetHeight < MOBILE_SHEET_FULL - 5 ? 'bg-white dark:bg-gray-900 rounded-t-[20px]' : ''
              }`}
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            >
              <div 
                className={`${selectedDetection ? 'pointer-events-none' : 'pointer-events-auto'} bg-white dark:bg-gray-900 flex flex-col relative ${
                  mobileSheetDragState.current.active ? '' : sheetTransitionClass
                } ${
                  mobileSheetHeight < MOBILE_SHEET_FULL - 5 ? 'rounded-t-[20px] shadow-[0_-4px_20px_rgba(0,0,0,0.15)]' : ''
                }`}
                style={{ 
                  height: mobileSheetHeight >= MOBILE_SHEET_FULL - 5
                    ? fullSheetHeightValue
                    : `${mobileSheetHeight}vh`,
                }}
              >
                {mobileSheetHeight < MOBILE_SHEET_FULL - 5 && (
                  <div
                    className="flex-shrink-0 cursor-grab active:cursor-grabbing touch-none select-none"
                    onPointerDown={handleSheetPointerDown}
                    onClick={(e) => {
                      if (!e.target.closest('button') && mobileSheetHeight <= MOBILE_SHEET_MAP + 5) {
                        handleMobileViewChange('list');
                      }
                    }}
                  >
                    <div className="flex justify-center pt-3 pb-2">
                      <div className="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
                    </div>
                    {mobileSheetHeight <= MOBILE_SHEET_MAP + 5 && (
                      <div className="px-4 pb-3 text-center">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {mapViewportResultCount ?? detections.length} result{(mapViewportResultCount ?? detections.length) !== 1 ? 's' : ''}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                <div 
                  className="flex-1 min-h-0 overflow-hidden"
                  style={{
                    opacity: mobileSheetHeight <= MOBILE_SHEET_MAP + 5 ? 0 : 1,
                    transition: sheetOpacityTransition,
                  }}
                >
                  {listPane}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {mobileSearchOverlay}
    </>
  );
};

export default CommunityPage;
